import { Button, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import { MessageDetailDialog } from 'sections/SystemMessages/components/MessageDetailDialog';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import * as TableConfig from 'sections/SystemMessages/tablesConfig';
import { systemMessagesTableTypes } from 'sections/SystemMessages/tables.types';
import { pagesEnum } from 'constants/ui.constants';
import { useNavigate, useParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from 'constants/pagination.constants';
import { StateScope, SYSTEM_MESSAGES_INITIAL_SORT_BY, SYSTEM_MESSAGES_INITIAL_SORT_ORDER } from 'constants/system-messages.constants';
import { getSystemMessagesError, setError } from 'state/reducers/system-messages.reducer';
import { WarningDialog } from 'components/WarningDialog';
import { useSystemMessagesTable } from 'sections/SystemMessages/hooks/useSystemMessagesTable';

export const CompanySystemMessagesTable = () => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const companyId = id ? parseInt(id) : undefined;
	const error = useAppSelector(getSystemMessagesError);
	const table = useSystemMessagesTable({ scope: StateScope.Company, companyId });

	const canCreate = hasPermission([permissionEnums.CREATE_SYSTEM_MESSAGE_COMPANY]);

	const handleCreate = () => {
		navigate(`/${pagesEnum.COMPANY_FEATURE_CREATE}/system-messages`);
	};

	return (
		<Stack spacing={3}>
			<WarningDialog
				open={error.exist}
				subTitle={error.text ? t(error.text) : ''}
				handleCancel={() => dispatch(setError({ exist: false }))}
			/>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Stack>
					<Typography variant="h6">{t('page.systemMessages.cs.tab.title')}</Typography>
					<Typography variant="caption">{t('page.systemMessages.cs.tab.subtitle')}</Typography>
				</Stack>
				{canCreate && (
					<Button variant="contained" onClick={handleCreate}>
						{t('page.systemMessages.action.createNew')}
					</Button>
				)}
			</Stack>
			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{table.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowsClass="alignTop"
				data={TableConfig.tableData(table.rows)}
				defaultSortBy={SYSTEM_MESSAGES_INITIAL_SORT_BY}
				defaultOrderBy={SYSTEM_MESSAGES_INITIAL_SORT_ORDER}
				columns={TableConfig.buildColumns(systemMessagesTableTypes.COMPANY, t)}
				rowAction={table.handleRow}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: table.total,
					hasMore: table.rows.length === DEFAULT_PAGE_SIZE && (table.currentPage + 1) * DEFAULT_PAGE_SIZE < table.total,
					currentPage: table.currentPage,
					onPageChange: table.handlePageChange,
					onSortChange: table.handleSortChange,
					isLoading: table.isLoading,
				}}
			/>
			<MessageDetailDialog open={table.detailOpen} onClose={table.closeDetail} disableReadOnly />
		</Stack>
	);
};
